import type { ClientTypes } from '@/common/hooks/referrals/types';
import { InteractionType } from '@/common/types/interaction';
import {
  formatTeamMemberId,
  formatTeamMemberIds,
} from '@/common/utils/formatTeamMemberId';
import {
  TXM_LEASES_PATH,
  TXM_LISTINGS_PATH,
  TXM_OFFERS_PATH,
} from '@/constants';
import type { CoreSchema } from '$api-clients/generated';
import type { AgentSelectOption } from '$form-fields';
import { ReferralErrorCode } from '../types';
import type { FieldValues } from '../views/NewReferral/types';

const referralErrorMessages: Record<string, string> = {
  [InteractionType.offer]: 'Offers can only be created for buyers.',
  [InteractionType.listing]: 'Listings can only be created for sellers.',
  [InteractionType.clientGroup]:
    'Buyer Agreements can only be created for buyers.',
  landlord: 'Leases (landlord) can only be created for landlords.',
  tenant: 'Leases (tenant) can only be created for tenants.',
  dual: 'Leases (dual) can only be created for landlords or tenants.',
};

export const getReferralErrorMessage = (
  code: ReferralErrorCode,
  interactionType: string,
  leaseRepresentation?: 'landlord' | 'tenant' | 'dual',
  propertyState?: string | null,
): string => {
  switch (code) {
    case ReferralErrorCode.USER_DOES_NOT_HAVE_SUBMIT_PERMISSIONS_FOR_USER:
      return 'You do not have permission to create a transaction on behalf of the transacting agent.';
    case ReferralErrorCode.TRANSACTING_AGENT_CANNOT_TRANSACT:
      return `The transacting agent doesn’t have a valid license${propertyState ? ` in ${propertyState}` : ''}.`;
    case ReferralErrorCode.REFERRAL_CLIENT_MISMATCH:
      return referralErrorMessages[
        interactionType === InteractionType.lease && leaseRepresentation
          ? leaseRepresentation
          : interactionType
      ];
    default:
      return '';
  }
};

const isReferralErrorCode = (
  errorCode: unknown,
): errorCode is ReferralErrorCode => {
  return (
    typeof errorCode === 'string' &&
    Object.values(ReferralErrorCode).includes(errorCode as ReferralErrorCode)
  );
};

export const getReferralErrorCode = (
  error:
    | CoreSchema.paths['/interactions']['post']['responses']['4XX']['content']['application/json']
    | string
    | undefined,
): ReferralErrorCode | undefined => {
  if (!error) return undefined;

  if (typeof error === 'object' && 'code' in error) {
    const errorCode = error.code;
    if (isReferralErrorCode(errorCode)) {
      return errorCode;
    }
  }

  return undefined;
};

export const getIsSending = (referral: Pick<FieldValues, 'referralType'>) => {
  return referral.referralType === 'sending';
};

export const getFirstAgentFieldName = (
  referral: Pick<FieldValues, 'referralType'>,
) => {
  const isSending = getIsSending(referral);
  return `${isSending ? 'referring' : 'transacting'}Agent` as const;
};

export const getSecondAgentFieldName = (
  referral: Pick<FieldValues, 'referralType'>,
) => {
  const isSending = getIsSending(referral);
  return `${isSending ? 'transacting' : 'referring'}Agent` as const;
};

export const getFirstAgent = (
  referral: Pick<
    FieldValues,
    'referralType' | 'transactingAgent' | 'referringAgent'
  >,
): FieldValues['referringAgent'] => {
  const isSending = getIsSending(referral);
  return !isSending ? referral.transactingAgent : referral.referringAgent;
};

export const getSecondAgent = (
  referral: Pick<
    FieldValues,
    'referralType' | 'transactingAgent' | 'referringAgent'
  >,
): FieldValues['referringAgent'] => {
  const isSending = getIsSending(referral);
  return isSending ? referral.transactingAgent : referral.referringAgent;
};

type AgentToMatch = Pick<
  NonNullable<FieldValues['transactingAgent']>,
  'userId' | 'teamId'
>;
export const findAgentTeamMembership = (
  agentToMatch: AgentToMatch | undefined,
  teamMemberships: AgentSelectOption[],
) => {
  if (!agentToMatch) return undefined;
  return teamMemberships.find((agent) =>
    formatTeamMemberIds(agent).includes(
      formatTeamMemberId(agentToMatch.userId ?? '', agentToMatch.teamId ?? ''),
    ),
  );
};

export const getInteractionButtonLinks = ({
  referralId,
  clientTypes,
}: {
  referralId: string;
  clientTypes: ClientTypes | undefined;
}): {
  href?: string;
  onClick?: () => Promise<void>;
  disabled?: boolean;
  buttonText: string;
}[] => {
  const options = [];

  if (clientTypes?.referralBuyer) {
    options.push({
      href: `${TXM_OFFERS_PATH}/new?referralId=${referralId}`,
      buttonText: 'Offer',
    });
  }

  if (clientTypes?.referralLandlord || clientTypes?.referralTenant) {
    options.push({
      href: `${TXM_LEASES_PATH}/new?referralId=${referralId}`,
      buttonText: 'Lease',
    });
  }

  if (clientTypes?.referralSeller) {
    options.push({
      href: `${TXM_LISTINGS_PATH}/new?referralId=${referralId}`,
      buttonText: 'Listing',
    });
  }
  return options;
};
